// src/components/CardPaymentsModal.js — card payments Cardknox approved that the app couldn't match to anyone
// [marker: pending-tickets v1] [marker: card-details v1]
// Shows each one with the card holder's name; staff pick the ticket order or invoice it is for and press
// "Record payment" (the order becomes Paid and the guest gets their confirmation), or "Not for the app".
// pendingOrders (optional): [{ ref, who, amount, created }] — this event's unpaid orders, offered first.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const usd = (v) => (parseFloat(v) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const when = (s) => {
  if (!s) return '';
  const d = new Date(String(s).replace(' ', 'T'));
  return isNaN(d) ? s : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

function PaymentRow({ p, options, onRecord, onIgnore, busy }) {
  const [choice, setChoice] = useState(p.reference || '');
  const [typed, setTyped] = useState('');
  const ref = choice === '__other' ? typed.trim() : choice;
  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{when(p.when)}</td>
      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{usd(p.amount)}</td>
      <td style={{ fontSize: '0.85rem' }}>{p.name || '—'}{(p.last4 || p.auth) ? <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{[p.last4 && `····${p.last4}`, p.auth && `auth ${p.auth}`, `ref ${p.refnum}`].filter(Boolean).join(' · ')}</div> : null}{p.description ? <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{p.description}</div> : null}</td>
      <td style={{ minWidth: 260 }}>
        <select className="form-select" style={{ fontSize: '0.8rem', height: 32, padding: '2px 6px' }} value={choice} onChange={e => setChoice(e.target.value)}>
          <option value="">— choose the order or invoice —</option>
          {options.map(o => <option key={o.ref} value={o.ref}>{o.ref} — {o.who || '(no name)'}{o.amount != null ? ` · ${usd(o.amount)}` : ''}</option>)}
          <option value="__other">Other — type an order or invoice number…</option>
        </select>
        {choice === '__other' && (
          <input className="form-input" style={{ marginTop: 4, fontSize: '0.8rem', height: 32 }} placeholder="ORD-2026-000123 or INV-2026-000050"
                 value={typed} onChange={e => setTyped(e.target.value)} />
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !ref} onClick={() => onRecord(p, ref)}>Record payment</button>{' '}
        <button className="btn btn-ghost btn-sm" disabled={busy} title="This charge isn't for a ticket or invoice in the app — hide it" onClick={() => onIgnore(p)}>Not for the app</button>
      </td>
    </tr>
  );
}

export default function CardPaymentsModal({ onClose, onDone, pendingOrders = [] }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setErr(''); setData(null);
    api.get('/card-payments/unmatched?days=90').then(r => setData(r.data)).catch(e => setErr(e.response?.data?.error || 'Could not load the payments from Cardknox.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const optionsFor = (p) => {
    const seen = new Set(); const out = [];
    const add = (o) => { if (o && o.ref && !seen.has(o.ref)) { seen.add(o.ref); out.push(o); } };
    if (p.reference) add({ ref: p.reference, who: (p.matchedBy || '').replace(/^amount \+ name \((.*)\)$/, '$1') });
    (p.possible || []).forEach(add);
    const amt = Math.round((parseFloat(p.amount) || 0) * 100);
    pendingOrders.filter(o => Math.round((parseFloat(o.amount) || 0) * 100) === amt).forEach(add);
    pendingOrders.forEach(add);
    return out;
  };
  const record = async (p, reference) => {
    setBusy(true);
    try {
      const r = await api.post('/card-payments/attach', { refnum: p.refnum, reference });
      toast.success(`${usd(p.amount)} recorded on ${r.data.reference}.`);
      onDone && onDone(); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not record it.'); }
    finally { setBusy(false); }
  };
  const ignore = async (p) => {
    if (!window.confirm(`Hide the ${usd(p.amount)} payment from ${p.name || 'this card'}? Only do this if it isn't for a ticket or invoice in the app.`)) return;
    setBusy(true);
    try { await api.post('/card-payments/ignore', { refnum: p.refnum }); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not hide it.'); }
    finally { setBusy(false); }
  };
  const rows = data ? [...(data.suggested || []), ...(data.unmatched || [])] : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 1100, width: '96vw' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Card payments not matched yet</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginTop: 0 }}>
            These cards were charged in Cardknox (last 90 days), but the app couldn’t tell which ticket order or invoice each one was for.
            Pick the right one and press <strong>Record payment</strong> — the order becomes Paid and the guest gets their confirmation email.
          </p>
          {err && <div style={{ color: 'var(--danger)', marginBottom: 10 }}>{err} <button className="btn btn-ghost btn-sm" onClick={load}>Try again</button></div>}
          {!data && !err && <div className="spinner" />}
          {data && !rows.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--success)' }}>✓ Every card payment is matched.</div>}
          {rows.length > 0 && (
            <div className="table-wrap" style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table>
                <thead><tr><th>Charged</th><th>Amount</th><th>Name on card</th><th>It’s for</th><th></th></tr></thead>
                <tbody>
                  {rows.map(p => <PaymentRow key={p.refnum} p={p} options={optionsFor(p)} onRecord={record} onIgnore={ignore} busy={busy} />)}
                </tbody>
              </table>
            </div>
          )}
          {data && data.ignored > 0 && <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 8 }}>{data.ignored} payment(s) marked “not for the app” are hidden.</div>}
        </div>
      </div>
    </div>
  );
}
